import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@repo/ui'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

type ListPaginationProps = {
  page: number
  totalPages: number
  pageSize: number
  totalCount: number
  hasPrevious: boolean
  hasNext: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  pageSizeOptions?: number[]
  className?: string
}

export function ListPagination({
  page,
  totalPages,
  pageSize,
  totalCount,
  hasPrevious,
  hasNext,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  className,
}: ListPaginationProps) {
  const { t } = useTranslation('common')

  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalCount)

  return (
    <div
      className={cn(
        'flex flex-col items-stretch gap-2 md:flex-row md:items-center md:justify-between',
        className,
      )}
    >
      <div className="text-muted-foreground flex flex-wrap items-start justify-between gap-x-4 gap-y-2 text-xs sm:items-center md:justify-start">
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap">{t('pagination.pageSize')}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="h-8! w-14 px-2 py-1 md:h-9 md:w-18 md:px-3">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!hasPrevious}
              onClick={() => onPageChange(1)}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!hasPrevious}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-3 text-xs whitespace-nowrap">
              {page} / {totalPages || 1}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!hasNext}
              onClick={() => onPageChange(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!hasNext}
              onClick={() => onPageChange(totalPages)}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>

          {totalCount > 0 && (
            <span className="whitespace-nowrap">
              {t('pagination.showing', { from, to, total: totalCount })}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
